import { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import { useQueryClient } from '@tanstack/react-query'

import { toast } from '@/hooks/useToast'
import {
  ANALYTICS_TRACKING_KEY,
  ANALYTICS_TIMEOUT_MINUTES,
  AnalyticsTrackingItem,
} from '@/types/analytics_monitor.type'
import { useCheckOnyxChatStatus } from '@/queries/conversation/useCheckOnyxChatStatus'
import { OnyxChatStatus } from '@/types/conversation.type'
import MessageQueryKeyGenerator from '@/queries/message/MessageQueryKeyGenerator'
import { useAuth } from '@/hooks/useAuth'

interface UseTrackAnalyticsCacheProps {
  messageId: string
  conversationId: string
  onComplete?: () => void
}

export const useTrackAnalyticsCache = ({
  messageId,
  conversationId,
  onComplete,
}: UseTrackAnalyticsCacheProps) => {
  const completedRef = useRef(false)
  const router = useRouter()
  const queryClient = useQueryClient()
  const { user } = useAuth()

  const getTrackingItem = () => {
    const stored = localStorage.getItem(ANALYTICS_TRACKING_KEY)
    if (!stored) return null

    const items: AnalyticsTrackingItem[] = JSON.parse(stored)
    return items.find((item) => item.messageId === messageId)
  }

  // 10분 타임아웃 체크
  const isExpired = () => {
    const trackingItem = getTrackingItem()
    if (!trackingItem) return false

    const startedAt = new Date(trackingItem.startedAt)
    const now = new Date()
    const minutesDiff = (now.getTime() - startedAt.getTime()) / (1000 * 60)

    return minutesDiff >= ANALYTICS_TIMEOUT_MINUTES
  }

  const removeFromTracking = () => {
    const stored = localStorage.getItem(ANALYTICS_TRACKING_KEY)
    if (!stored) return

    const items: AnalyticsTrackingItem[] = JSON.parse(stored)
    const filtered = items.filter((item) => item.messageId !== messageId)

    if (filtered.length > 0) {
      localStorage.setItem(ANALYTICS_TRACKING_KEY, JSON.stringify(filtered))
    } else {
      localStorage.removeItem(ANALYTICS_TRACKING_KEY)
    }
  }

  // useCheckOnyxChatStatus로 폴링 (5초 간격)
  const {
    data: statusData,
    isError,
    error,
  } = useCheckOnyxChatStatus({
    conversationId,
    messageId,
    shouldPoll: !completedRef.current && !isExpired(),
    enabled: !!conversationId && !!messageId && !completedRef.current,
  })

  // 404 에러 시 폴링 중단 및 tracking 정리 (삭제된 conversation 등)
  useEffect(() => {
    if (isError && !completedRef.current) {
      const status = (error as any)?.response?.status
      if (status === 404) {
        completedRef.current = true
        removeFromTracking()
      }
    }
  }, [isError, error, messageId])

  // 타임아웃 체크 (1분 간격)
  useEffect(() => {
    const checkExpiration = () => {
      if (isExpired() && !completedRef.current) {
        completedRef.current = true
        removeFromTracking()
      }
    }

    checkExpiration()
    const interval = setInterval(checkExpiration, 60000)

    return () => clearInterval(interval)
  }, [messageId])

  // 완료 감지
  useEffect(() => {
    if (statusData?.data === OnyxChatStatus.Success && !completedRef.current) {
      completedRef.current = true

      const trackingItem = getTrackingItem()
      if (!trackingItem) return

      // 메시지 캐시 invalidate
      if (user?.id) {
        queryClient.invalidateQueries({
          queryKey: MessageQueryKeyGenerator.index(user.id, conversationId, 1),
          refetchType: 'all',
        })
      }

      // Artifact Q&A conversations reopen on the artifact viewer, not the
      // analytics chat — mirror the Recent-list routing (see conversationRoute).
      const artifactId = trackingItem.artifactId

      // 유저가 해당 conversation 페이지에 있지 않을 때만 알람 표시
      const isOnCurrentConversation = artifactId
        ? router.pathname === '/artifacts/[artifactId]' && router.query.artifactId === artifactId
        : router.pathname === '/analytics' && router.query.conversation_id === conversationId

      if (!isOnCurrentConversation) {
        const truncatedQuery =
          trackingItem.query.length > 30
            ? trackingItem.query.slice(0, 30) + '...'
            : trackingItem.query

        toast({
          type: 'alarm',
          text: `Analytics answer for "${truncatedQuery}" is ready.`,
          duration: 500000,
          onClick: () => {
            router.push(
              artifactId
                ? `/artifacts/${encodeURIComponent(artifactId)}`
                : `/analytics?conversation_id=${conversationId}`
            )
          },
        })
      }

      removeFromTracking()
      onComplete?.()
    }
  }, [statusData?.data, conversationId, user?.id])

  return {
    isComplete: completedRef.current,
  }
}
